import dayjs from "dayjs";
import {
  addZonedDays,
  instantFromZonedWallClock,
  zonedDayString,
} from "@/shared/utils/date/zoned-wall-clock.ts";

/**
 * Shared six-hour SHIFT fixtures that tile a full 24h day, used by the
 * app-wide date-range picker and the post-login default range.
 *
 * A shift is a wall-clock window AT THE SITE, so every function here takes the
 * location's IANA zone (resolved from `useTimezoneStore`) and resolves both the
 * hours and the calendar day there.
 */
export interface ShiftDefinition {
  key: string;
  /** `common` key — a shift name is rendered, so it cannot be a literal here. */
  labelKey:
    | "shifts.night"
    | "shifts.morning"
    | "shifts.afternoon"
    | "shifts.evening";
  startHour: number;
  endHour: number;
}

/**
 * The four fixed shifts, in chronological order across the day.
 * `endHour` 24 rolls to the next day's 00:00 when materialised as a Date.
 */
export const SHIFT_DEFINITIONS: ShiftDefinition[] = [
  { key: "shift-night", labelKey: "shifts.night", startHour: 0, endHour: 6 },
  { key: "shift-morning", labelKey: "shifts.morning", startHour: 6, endHour: 12 },
  { key: "shift-afternoon", labelKey: "shifts.afternoon", startHour: 12, endHour: 18 },
  { key: "shift-evening", labelKey: "shifts.evening", startHour: 18, endHour: 24 },
];

/**
 * Builds the concrete [from, to) window for `shift` on the calendar day that
 * `now` falls on IN `timeZone`. Both the day and the hours resolve there, so a
 * Prague operator picking the morning shift of a São Paulo site gets that site's
 * 06:00–12:00 — and never the previous site day, which the old browser-local
 * `now.getDate()` produced between 00:00 and 05:00 Prague.
 * `endHour` 24 correctly rolls to next-day 00:00.
 */
export const getShiftRange = (
  shift: ShiftDefinition,
  now: Date,
  timeZone: string,
): { from: Date; to: Date } => {
  const day = zonedDayString(now, timeZone);
  const at = (hour: number) =>
    instantFromZonedWallClock(`${day} ${String(hour).padStart(2, "0")}:00:00`, timeZone);
  // Next-day 00:00 via calendar-day arithmetic, so a DST day stays exact.
  return {
    from: shift.startHour >= 24 ? addZonedDays(at(0), 1, timeZone) : at(shift.startHour),
    to: shift.endHour >= 24 ? addZonedDays(at(0), 1, timeZone) : at(shift.endHour),
  };
};

/**
 * Returns the shift whose window contains `now` at the site (defaults to the
 * night shift). The hour is read in `timeZone`, not the viewer's zone.
 */
export const getCurrentShift = (now: Date, timeZone: string): ShiftDefinition => {
  const h = dayjs(now).tz(timeZone).hour();
  return SHIFT_DEFINITIONS.find((s) => h >= s.startHour && h < s.endHour) ?? SHIFT_DEFINITIONS[0];
};
